import React, { useEffect, useState } from 'react';
import { SimpleGrid, useToast } from '@chakra-ui/react';
import {
  MdAgriculture,
  MdApartment,
  MdAssignment,
  MdBarChart,
  MdConstruction,
  MdGroups,
  MdInventory2,
  MdMap,
  MdPeopleAlt,
  MdSettings,
  MdUploadFile,
} from 'react-icons/md';
import { getDashboardCounts } from '../../../repositories/dashboard.repository';
import {
  HubCard,
  HubError,
  HubLoading,
  HubMetricCard,
  HubPageHeader,
  HubSectionLabel,
} from './hubUi';

export default function HubHome({ push }) {
  const toast = useToast();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      setData(await getDashboardCounts());
    } catch (e) {
      setError(e.message || 'Failed to load dashboard');
      toast({ title: e.message || 'Failed to load dashboard', status: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) return <HubLoading />;
  if (error) return <HubError message={error} onRetry={load} />;

  const eq = data?.equipment || {};
  const vendors = data?.vendors || {};
  const sp = vendors.serviceProviders?.total || 0;
  const ag = vendors.agencies?.total || 0;
  const members = vendors.total ?? sp + ag;
  const indents = data?.indents || {};
  const bus = data?.businessUnits || {};
  const openIndents =
    indents.open ??
    (Number(indents.pending) || 0) +
      (Number(indents.recommended) || 0) +
      (Number(indents.assigned) || 0) +
      (Number(indents.inProgress) || 0);

  return (
    <>
      <HubPageHeader title="Dashboard hub" />

      <HubSectionLabel>At a glance</HubSectionLabel>
      <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing={3} mb={5}>
        <HubMetricCard
          icon={MdAgriculture}
          label="Equipment"
          total={String(eq.total ?? 0)}
          alert={{
            label: 'Pending Verification',
            value: eq.pendingKyc ?? 0,
          }}
          metrics={[
            { label: 'Available', value: String(eq.available ?? 0) },
            { label: 'Deployed', value: String(eq.deployed ?? 0) },
            { label: 'Pending', value: String(eq.pendingKyc ?? 0) },
          ]}
          onClick={() => push({ id: 'equipmentHub', label: 'Equipment' })}
        />
        <HubMetricCard
          icon={MdPeopleAlt}
          label="Users"
          total={String(members)}
          alert={{
            label: 'Pending Verification',
            value: vendors.pending ?? 0,
          }}
          metrics={[
            { label: 'Sellers', value: String(sp) },
            { label: 'Buyers', value: String(ag) },
            { label: 'Pending', value: String(vendors.pending ?? 0) },
          ]}
          onClick={() => push({ id: 'membersHub', label: 'Users' })}
        />
        <HubMetricCard
          icon={MdAssignment}
          label="Indents"
          total={String(indents.total ?? 0)}
          alert={
            openIndents > 0
              ? { label: 'open', value: openIndents }
              : null
          }
          metrics={[
            { label: 'Open', value: String(openIndents) },
            { label: 'Released', value: String(indents.released ?? 0) },
            { label: 'Withdrawn', value: String(indents.cancelled ?? 0) },
          ]}
          onClick={() => push({ id: 'indentsHub', label: 'Indents' })}
        />
        <HubMetricCard
          icon={MdGroups}
          label="Staff"
          total={String(data?.staff?.total ?? 0)}
          metrics={[
            { label: 'Active', value: String(data?.staff?.active ?? 0) },
            { label: 'Inactive', value: String(Math.max(0, (data?.staff?.total ?? 0) - (data?.staff?.active ?? 0))) },
            { label: 'Roles', value: '—' },
          ]}
          onClick={() => push({ id: 'staffHub', label: 'Staff' })}
        />
      </SimpleGrid>

      <HubSectionLabel>Master data</HubSectionLabel>
      <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing={3} mb={5}>
        <HubCard
          icon={MdMap}
          label="Villages"
          value={String(data?.villages?.total ?? 0)}
          hint="Add / edit villages →"
          onClick={() => push({ id: 'masterVillages', label: 'Villages' })}
        />
        <HubCard
          icon={MdApartment}
          label="Business units"
          value={String(bus.total ?? 0)}
          hint={`${bus.sites ?? 0} sites · add / edit →`}
          onClick={() =>
            push({ id: 'masterBusinessUnits', label: 'Business units' })
          }
        />
        <HubCard
          icon={MdSettings}
          label="Master data hub"
          value="Open"
          hint="Villages · BUs · equipment types"
          onClick={() => push({ id: 'masterHub', label: 'Master data' })}
        />
        <HubCard
          icon={MdUploadFile}
          label="Bulk import"
          value="Excel"
          hint="Template → upload → add"
          onClick={() => push({ id: 'bulkImportHub', label: 'Bulk import' })}
        />
      </SimpleGrid>

      <HubSectionLabel>Reports</HubSectionLabel>
      <SimpleGrid columns={{ base: 1, sm: 3 }} spacing={3}>
        <HubCard
          icon={MdBarChart}
          label="Village report"
          value={String(data?.villages?.total ?? 0)}
          hint="Utilization by village"
          onClick={() => push({ id: 'villageReport', label: 'Village report' })}
        />
        <HubCard
          icon={MdConstruction}
          label="BU / site report"
          value={String(bus.sites ?? 0)}
          hint={`${bus.total ?? 0} business units · utilization`}
          onClick={() => push({ id: 'buReport', label: 'BU utilization' })}
        />
        <HubCard
          icon={MdInventory2}
          label="Materials"
          value={String(data?.materials?.total ?? 0)}
          hint={`${data?.materials?.pending ?? 0} pending review`}
          onClick={() => push({ id: 'membersHub', label: 'Users' })}
        />
      </SimpleGrid>
    </>
  );
}
